// PERIOD CLOSE — batch 5's rebuild of Periods (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on).
//
// ASTRA'S SHAPE (close-1440 / close-390): the title, then ONE panel for the month on screen — its state at the right,
// the checks first (a failed one is a line, its detail behind Details), then the close's posting by asset — and one
// gold "Review close" that opens the confirm naming the month and its figures. Beside it on the laptop, one note and
// the previous closes (retained-earnings history) behind a tap; on the phone the history fold sits under the panel.
//
// THE BEHAVIOUR IS TODAY'S (screens/Periods.tsx), copied: the same queries and keys (['periods'], ['period-preview', m])
// and the passive per-month observers the month states are drawn from; the same mutation (api.closePeriod) and its
// invalidations; `canClose` verbatim — the preview read, settled (`fetchStatus === 'idle'`), not failed, the month
// ended, no failed check, not already closed; the confirm names the month and the amounts it posts, the reversal note
// one tap down; the close's refusals (already closed, busy, out of order, a check failed, forbidden for a read-only
// sign-in, a close whose outcome is unknown) are the server's own words in ServerLine — a 5xx is said whole. Closing
// and reopening stay the server's to permit (requireFullAccess); this screen grants and withholds nothing.
//
// WHAT MOVED: on the phone the month strip is one select in the head (each month's state in its option); on the laptop
// every month's state is one quiet line under the title again (restored 1 Nov 2026); the month shown first is
// the one the desk must close next — the oldest ended month still open — or, when none is, the running month (the
// strip used to show every month's state at a glance; a select hides them, so the month that needs him leads). The
// three-figure table is one line per asset (what reaches retained earnings), its two parts behind "Breakdown". The
// "Already in retained earnings" table is "Previous closes", folded. The help paragraphs are gone.
import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PeriodPreview, type PeriodsResponse } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { fmtDate, fmtDay } from '../../lib/format';
import { Notice, PageHead, Panel, ReadFailed, ServerLine, SkeletonRows, StateLine, ReadNotice } from '../../components/v2/kit';
import { Pill } from '../../components/Pill';
import {
  GateFailure, MONTH_WORD, MonthPill, Postings, PreviousCloses, Step, amountList, monthWords, monthsSince, type MonthState,
} from '../../components/v2/close/parts';

export function CloseV2() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const periodsQ = useQuery<PeriodsResponse>({
    queryKey: ['periods'],
    queryFn: () => api.listPeriods(),
  });

  const months = periodsQ.data ? monthsSince(periodsQ.data.regime_start) : [];
  const rowFor = (p: string) => periodsQ.data?.periods.find((x) => x.period === p);
  const statusOf = (p: string): 'open' | 'closed' | 'unopened' => rowFor(p)?.status ?? 'unopened';
  // THE MONTH THAT LEADS: the oldest ended month not yet closed (months close in order, so it is the next to close);
  // when every ended month is closed, the running month — today's default.
  const nextToClose = [...months.slice(1)].reverse().find((m) => statusOf(m) !== 'closed');
  const active = selected ?? nextToClose ?? months[0] ?? null;

  const previewQ = useQuery<PeriodPreview>({
    queryKey: ['period-preview', active],
    queryFn: () => api.previewPeriod(active!),
    enabled: !!active,
  });

  // ONE OBSERVER PER MONTH, FETCHING NOTHING (`enabled: false`): each month's state is drawn from its own read, and a
  // late answer or a failed check that arrives late changes it. Nothing is fetched for a month he has not opened.
  const reads = useQueries({
    queries: months.map((m) => ({
      queryKey: ['period-preview', m],
      queryFn: () => api.previewPeriod(m),
      enabled: false,
    })),
  });

  const close = useMutation({
    mutationFn: () => api.closePeriod(active!),
    onSuccess: () => {
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['periods'] });
      qc.invalidateQueries({ queryKey: ['period-preview'] });
      qc.invalidateQueries({ queryKey: ['balance-sheet'] });
    },
  });

  const preview = previewQ.data;
  const gatesFailed = preview?.gates.some((g) => !g.ok) ?? false;
  const activeClosed = active ? statusOf(active) === 'closed' : false;
  // Periods.tsx, verbatim: disabled while the preview is refetching (the figures the confirm names must be the ones the
  // close will post), never over a read that FAILED (its last good data could still pass), and "idle" exactly — a read
  // paused by a lost connection is not settled either.
  const canClose = !!preview && preview.period_ended && !gatesFailed && !activeClosed && previewQ.fetchStatus === 'idle' && !previewQ.isError;
  const closedAt = activeClosed && preview ? rowFor(preview.period)?.closed_at ?? null : null;

  // A MONTH'S STATE (Periods.tsx chipState, verbatim): the running month is Open; an ended open month is Close blocked
  // when its last read has a failed check (through a failed refetch too), Ready to close only from a read that
  // succeeded and has settled, and To close otherwise.
  const chipState = (p: string): MonthState => {
    const st = statusOf(p);
    if (st !== 'open') return st;
    if (p === months[0]) return 'open';
    const read = reads[months.indexOf(p)];
    if (!read?.data) return 'ended';
    if (read.data.gates.some((g) => !g.ok)) return 'blocked';
    const idle = (p === active ? previewQ.fetchStatus : read.fetchStatus) === 'idle';
    if (read.data.period_ended && read.status === 'success' && idle) return 'ready';
    return 'ended';
  };

  const pick = (m: string) => { setSelected(m); setConfirming(false); };
  const rs = readState(previewQ);

  const monthSelect = months.length > 0 && active ? (
    <label className="w-full sm:w-auto">
      <span className="sr-only">Month</span>
      <select className="select w-full sm:w-auto" value={active} onChange={(e) => pick(e.target.value)}>
        {months.map((m) => <option key={m} value={m}>{monthWords(m)} · {MONTH_WORD[chipState(m)]}</option>)}
      </select>
    </label>
  ) : null;

  // EVERY MONTH'S STATE AT A GLANCE (the old month strip; his "restore most the lost things", 1 Nov 2026): one quiet line of
  // the months, each a tap that opens it, its state in its words; the one on screen in the text colour. The select stays
  // for the phone, where the line would wrap.
  const monthLine = months.length > 1 && active ? (
    <p className="-mt-2 mb-4 hidden flex-wrap gap-x-4 gap-y-1 text-meta lg:-mt-3 lg:mb-5 lg:flex" aria-label="Months">
      {months.map((m) => {
        const st = chipState(m);
        const tone = m === active ? 'text-tg-text font-medium' : st === 'ended' || st === 'ready' || st === 'blocked' ? 'text-warn' : 'text-tg-hint';
        return (
          <button key={m} type="button" className={`${tone} hover:text-tg-text`} aria-current={m === active ? 'true' : undefined} onClick={() => pick(m)}>
            {monthWords(m)} · {MONTH_WORD[st]}
          </button>
        );
      })}
    </p>
  ) : null;

  const head = <><PageHead title="Period close" actions={<span className="lg:hidden">{monthSelect}</span>} />{monthLine}</>;

  // The months: a first read waits, a failed one is one Notice and its Try again, nothing drawn under it.
  if (!periodsQ.data) {
    return (
      <>
        {head}
        {periodsQ.isError
          ? <ReadNotice what="the months" error={periodsQ.error} onRetry={() => { void periodsQ.refetch(); }} />
          : <Panel flush className="lg:max-w-[600px]"><SkeletonRows what="the months" rows={3} /></Panel>}
      </>
    );
  }

  const failed = preview?.gates.filter((g) => !g.ok) ?? [];
  const total = preview?.gates.length ?? 0;

  /** Step 1: the checks. "Checked" and "All N pass" only from a read that succeeded — an unread check never passes; a
   *  failed check stays on screen through a failed refresh. */
  // A closed month's passing checks are not news (Periods.tsx `quiet`); a failed one is never dropped.
  const showChecks = !!preview && (!activeClosed || failed.length > 0);
  const checks = !showChecks ? null : (
    <Step
      n={1}
      title="Checks"
      tone={failed.length > 0 ? 'warn' : rs === 'known' && total > 0 ? 'done' : 'neutral'}
      sub={failed.length > 0 ? `${failed.length} of ${total} failed`
        : rs !== 'known' ? 'Unknown — not read'
          : total === 0 ? 'None for this month' : `All ${total} pass`}
      state={failed.length === 0 && rs === 'known' && total > 0 ? <Pill tone="good">Checked</Pill> : undefined}
    >
      {failed.length > 0 && <ul className="space-y-2">{failed.map((g) => <GateFailure key={g.name} gate={g} />)}</ul>}
    </Step>
  );

  /** Step 2: what the close posts, by asset — or, for a closed month, what it posted. */
  const review = !preview ? null : (
    <Step
      n={showChecks ? 2 : 1}
      title={activeClosed ? 'Posted' : 'Review close'}
      tone={activeClosed ? 'done' : canClose ? 'warn' : 'neutral'}
      sub={activeClosed ? `Into retained earnings${closedAt ? ` · ${fmtDate(closedAt)}` : ''}` : 'Posting by asset'}
      state={activeClosed ? <Pill tone="good">Closed</Pill> : canClose ? <Pill tone="warn">Ready</Pill> : <Pill>Waiting</Pill>}
    >
      {preview.assets.length > 0
        ? <Postings assets={preview.assets} />
        : rs === 'known' && <p className="text-body text-tg-hint">Nothing in this month.</p>}
      {/* The old screen's rule, beside the closed month it applies to (restored 1 Nov 2026). */}
      {activeClosed && <p className="mt-3 text-meta text-tg-hint">Reopening needs a written reason and reverts its postings.</p>}
    </Step>
  );

  /** The one action, its reason when it cannot be pressed, the confirm, and the books' answer. A closed month has none;
   *  a running month says when it can close. */
  const action = !preview || activeClosed ? null : !preview.period_ended ? (
    <StateLine>Still running — closes from {fmtDay(preview.window.end)}.</StateLine>
  ) : confirming ? (
    <div className="space-y-3 rounded-lg bg-warn-bg p-3">
      {/* The consequence, with the figures — one line; that undoing leaves a reversal in the record is one tap down. */}
      <StateLine tone="warn" more="Undoing it leaves a reversal in the record.">
        Close {monthWords(preview.period)}? Posts {amountList(preview.assets)} into retained earnings.
      </StateLine>
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1 px-4 text-body lg:flex-none"
          disabled={close.isPending || !canClose}
          onClick={() => close.mutate()}
        >
          {close.isPending ? 'Closing…' : `Close ${monthWords(preview.period)}`}
        </button>
        <button type="button" className="btn-secondary shrink-0 px-4 text-body" onClick={() => setConfirming(false)}>Cancel</button>
      </div>
    </div>
  ) : (
    // The form's action at its foot, right — as every form's (the audit, 1 Nov 2026: it sat at the foot-left).
    <div className="flex flex-col gap-3 lg:items-end">
      {gatesFailed && (
        <p className="w-full rounded-lg border border-warn/25 bg-warn-bg px-4 py-3 text-body text-warn">Resolve the failed checks before closing.</p>
      )}
      <button type="button" className="btn-primary w-full px-4 text-body lg:w-auto" disabled={!canClose} onClick={() => { setSelected(preview.period); setConfirming(true); }}>
        Review close
      </button>
    </div>
  );

  return (
    <>
      {head}
      {/* The side column beside the month only from 1280 and never under 280 px — at 1024 it was ~124 px (the forms' rule, FormFrame). */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,600px)_minmax(280px,1fr)] xl:items-start">
        <Panel
          flush
          title={active ? monthWords(active) : ''}
          action={active ? <MonthPill state={chipState(active)} /> : undefined}
        >
          {/* The close's read: waiting is skeleton rows (never "nothing"); a failed first read is its line and Try again;
              a failed refresh keeps the month it had under one line and claims no pass from it. */}
          {!preview && rs !== 'failed' && <SkeletonRows what="the close" rows={2} />}
          {previewQ.isError && (
            <div className={`px-4 pt-4 lg:px-5 ${preview ? '' : 'pb-4'}`}>
              <ReadFailed tone={preview ? 'warn' : 'bad'} what="the close" error={previewQ.error} onRetry={() => { void previewQ.refetch(); }} />
            </div>
          )}
          {preview && <ol>{checks}{review}</ol>}
          {(action || close.isError) && (
            <div className="space-y-3 border-t border-stroke-soft px-4 py-4 lg:px-5">
              {action}
              {/* The books' answer to the close — a refusal in its own words; a 5xx or a lost answer whole (it says
                  whether the month was sealed). */}
              {close.isError && <ServerLine error={close.error} />}
            </div>
          )}
        </Panel>

        <aside className="space-y-1">
          <PreviousCloses legs={periodsQ.data.legs} />
          {/* The rule of a close, behind its tap (Astra, 1 Nov 2026: an instruction column on the page). */}
          <details className="group">
            <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1.5 text-body text-tg-hint hover:text-tg-text lg:min-h-10 [&::-webkit-details-marker]:hidden">
              <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden />About closing
            </summary>
            <p className="mt-1 text-meta text-tg-hint">Close one month at a time. Review each asset&apos;s posting; an unknown check cannot pass.</p>
          </details>
        </aside>
      </div>
    </>
  );
}
